import {
  extractLiquidVars,
  LIQUID_VIRTUAL_FIELDS,
  parseFieldMarker,
} from '@tronche2tek/shared-types';

import type { CardSides, PreflightInput } from '../../../api/cards';

/**
 * What the operator fills in for a single card: the template (saved or a custom
 * SVG), the side(s) to render and a value per extra Liquid field.
 */
export interface SingleCardFields {
  templateId: string;
  customTemplateFile: File | null;
  fieldValues: Record<string, string>;
  sides: CardSides;
}

/**
 * Typed explicitly: `useForm` derives its field paths from what this returns,
 * so inferring `null` for the File field would drop it from the paths.
 */
export function initialSingleCardFields(): SingleCardFields {
  return {
    templateId: '',
    customTemplateFile: null,
    fieldValues: {},
    sides: 'front',
  };
}

/**
 * The variables the operator has to type for this one card: everything the SVG
 * uses, minus the two the renderer injects, minus the ones the template already
 * binds to a source. A bound field is resolved per recipient from the defaults —
 * surfacing it here would show the raw `entra:givenName` marker in an input and
 * ship it as a literal value.
 */
export function extractExtraFields(
  svg: string,
  defaults: Record<string, string>,
): string[] {
  const vars = extractLiquidVars(svg);
  for (const v of LIQUID_VIRTUAL_FIELDS) {
    vars.delete(v);
  }
  for (const v of [...vars]) {
    if (parseFieldMarker(defaults[v])) {
      vars.delete(v);
    }
  }
  return [...vars];
}

function csvEscape(val: string): string {
  if (val.includes(',') || val.includes('"') || val.includes('\n')) {
    return `"${val.replace(/"/g, '""')}"`;
  }
  return val;
}

/** A one-row CSV: the header the renderer expects, then this recipient. */
export function singleRowCsv(
  upn: string,
  extraFields: string[],
  fieldValues: Record<string, string>,
): string {
  const headers = ['upn', ...extraFields];
  const row = [upn, ...extraFields.map((f) => fieldValues[f] ?? '')];
  return headers.map(csvEscape).join(',') + '\n' + row.map(csvEscape).join(',');
}

export interface SingleCardRequest {
  /** Which recipient list the template's type implies. */
  source: 'csv' | 'isee' | 'lucca';
  upn: string;
  templateId: string;
  /** The front SVG, resolved from the saved template or the custom upload. */
  resolvedSvg: string;
  sides: CardSides;
  /** Every extra field the generated side(s) use, in order. */
  extraFields: string[];
  /** The subset of `extraFields` flagged `form:`, sent as `values`. */
  formFields: string[];
  fieldValues: Record<string, string>;
}

/**
 * The pre-flight request for one card. ISEE and Lucca take a login list, so the
 * single recipient goes as a one-element array; anything else — 'other'
 * templates and custom SVG uploads — is posted as a one-row CSV, since that is
 * the only shape the CSV pipeline speaks.
 */
export function buildPreflightInput(req: SingleCardRequest): PreflightInput {
  const values = req.formFields.length
    ? Object.fromEntries(
        req.formFields.map((f) => [f, req.fieldValues[f] ?? '']),
      )
    : undefined;

  if (req.source === 'lucca') {
    return {
      source: 'lucca',
      templateId: req.templateId,
      logins: [req.upn],
      sides: req.sides,
      values,
    };
  }
  if (req.source === 'isee') {
    return {
      source: 'isee',
      logins: [req.upn],
      templateId: req.templateId || undefined,
      sides: req.sides,
      values,
    };
  }
  return {
    source: 'csv',
    csv: new File(
      [singleRowCsv(req.upn, req.extraFields, req.fieldValues)],
      'single.csv',
      { type: 'text/csv' },
    ),
    template: new File([req.resolvedSvg], 'template.svg', {
      type: 'image/svg+xml',
    }),
    templateId: req.templateId || undefined,
    sides: req.sides,
  };
}
